import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"
import { Globe, Phone } from "lucide-react"

import { CustomSipHeadersField } from "@/components/shared/forms/CustomSipHeadersField"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { useClearMediaEncryptionOutsideSipMode } from "@/hooks/useClearMediaEncryptionOutsideSipMode"
import { useConnectionMode } from "@/hooks/useConnectionMode"
import { useSipHeadersJson } from "@/hooks/useSipHeadersJson"
import {
  isMediaEncryptionValue,
  MediaEncryption,
} from "@/types/api/SimulatorType"

import {
  INBOUND_VOICE_TOOLTIPS,
  PhoneNumberField,
  SipAddressField,
} from "./SimulatorFormFields"

const MEDIA_ENCRYPTION_OPTIONS = [
  { label: "Disabled", value: MediaEncryption.DISABLED },
  { label: "SRTP", value: MediaEncryption.SRTP },
  { label: "DTLS", value: MediaEncryption.DTLS },
] as const

interface MultiPhoneInboundVoiceFieldsProps {
  phoneNumber: string
  onPhoneNumberChange: (value: string) => void
  phoneNumberIndex?: number
  onPhoneNumberIndexChange?: (value: number) => void
  widebandEnabled?: boolean
  onWidebandChange?: (enabled: boolean) => void
  customSipHeaders?: string
  onCustomSipHeadersChange?: (value: string) => void
  mediaEncryption?: string
  onMediaEncryptionChange?: (value: string) => void
  disabled?: boolean
  className?: string
}

export const MultiPhoneInboundVoiceFields: React.FC<
  MultiPhoneInboundVoiceFieldsProps
> = ({
  phoneNumber,
  onPhoneNumberChange,
  widebandEnabled,
  onWidebandChange,
  customSipHeaders,
  onCustomSipHeadersChange,
  mediaEncryption,
  onMediaEncryptionChange,
  disabled,
  className = "",
}) => {
  const parsedSipHeaders = useSipHeadersJson(customSipHeaders)

  const { inputMode, handleModeChange } = useConnectionMode({
    phoneNumber,
    onPhoneNumberChange,
    onModeChange: (newMode) => {
      if (newMode === "phone") {
        onWidebandChange?.(false)
      }
    },
  })

  const isSipMode = inputMode === "sip"
  useClearMediaEncryptionOutsideSipMode(
    isSipMode,
    phoneNumber,
    mediaEncryption,
    onMediaEncryptionChange,
  )
  const mediaEncryptionSelectValue =
    mediaEncryption == null || isMediaEncryptionValue(mediaEncryption)
      ? (mediaEncryption ?? MediaEncryption.DISABLED)
      : ""

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FieldLabel tooltip={INBOUND_VOICE_TOOLTIPS.connectionType}>
          Connection Type
        </FieldLabel>
        <Tabs value={inputMode} onValueChange={handleModeChange}>
          <TabsList variant="pill" aria-label="Connection Type">
            <TabsTrigger value="phone" variant="pill" disabled={disabled}>
              <Phone className="w-4 h-4" />
              Phone Number
            </TabsTrigger>
            <TabsTrigger value="sip" variant="pill" disabled={disabled}>
              <Globe className="w-4 h-4" />
              SIP Address
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {inputMode === "phone" ? (
        <PhoneNumberField
          value={phoneNumber}
          onChange={onPhoneNumberChange}
          disabled={disabled}
          className={className}
          labelTooltip={INBOUND_VOICE_TOOLTIPS.phoneNumber}
        />
      ) : (
        <>
          <SipAddressField
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            widebandEnabled={widebandEnabled}
            onWidebandChange={onWidebandChange}
            disabled={disabled}
            className={className}
            labelTooltip={INBOUND_VOICE_TOOLTIPS.sipAddress}
          />
          {onCustomSipHeadersChange && (
            <CustomSipHeadersField
              value={parsedSipHeaders}
              onChange={onCustomSipHeadersChange}
            />
          )}
          {onMediaEncryptionChange && (
            <div className="space-y-2">
              <FieldLabel>Media Encryption</FieldLabel>
              <Select
                value={mediaEncryptionSelectValue}
                onValueChange={onMediaEncryptionChange}
                disabled={disabled}
              >
                <SelectTrigger aria-label="Media Encryption">
                  <SelectValue
                    placeholder={
                      mediaEncryptionSelectValue === ""
                        ? "Unsupported saved value; choose a replacement"
                        : undefined
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {MEDIA_ENCRYPTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      )}
    </div>
  )
}
